// Two Pointers — curated interview problems with multiple worked approaches.
// Same shape as dsaProblems/arrays.js and dsaProblems/hashing.js: concise steps, a
// "why & when to use" close per approach, and an interactive visualizer wherever it
// clarifies the pointer movement.

const twoPointersProblems = [
  {
    id: 'two-sum-ii-sorted',
    title: 'Two Sum II — Input Array Is Sorted',
    difficulty: 'Easy',
    leetcode: { number: 167, url: 'https://leetcode.com/problems/two-sum-ii-input-array-is-sorted/' },
    statement:
      'Given a 1-indexed array of integers numbers that is already sorted in non-decreasing order, find two ' +
      'numbers that add up to a specific target. Return the indices of the two numbers (1-indexed) as an array. ' +
      'You may not use the same element twice.',
    example: {
      input: 'numbers = [2, 7, 11, 15], target = 9',
      output: '[1, 2]',
      note: 'numbers[1] + numbers[2] (1-indexed) = 2 + 7 = 9.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Check Every Pair)',
        why:
          "The direct reading of the problem: try every pair of indices and check whether they sum to target. " +
          "Correct, but it never uses the one fact the problem statement hands you for free — the array is sorted.",
        steps: [
          'Fix an index i for every position in the array.',
          'Try every later index j > i as the second number.',
          'If numbers[i] + numbers[j] equals target, return both indices (converted to 1-indexed).',
          'Every pair is checked in the worst case — O(n²), ignoring the sortedness entirely.',
        ],
        visual: {
          arrays: [{ label: 'numbers (target = 9)', values: [2, 7, 11, 15] }],
          varNames: [],
          steps: [
            { highlights: { numbers: { primary: [0], secondary: [1] } }, note: '2 + 7 = 9. Match! Return 1-indexed [1, 2].' },
          ],
        },
        code:
`public int[] twoSumBruteForce(int[] numbers, int target) {
    for (int i = 0; i < numbers.length; i++) {
        for (int j = i + 1; j < numbers.length; j++) {
            if (numbers[i] + numbers[j] == target) {
                return new int[] { i + 1, j + 1 };
            }
        }
    }
    return new int[] {};
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
        whenToUse:
          "Only useful to confirm you understand the pairing requirement — the moment you notice \"already sorted\" " +
          "in the problem statement, that's a direct signal to reach for opposite-direction two pointers instead.",
      },
      {
        name: 'Approach 2 — Optimized: Two Pointers From Both Ends',
        why:
          "Because the array is sorted, the sum of the outermost pair moves in exactly one predictable direction " +
          "as each pointer shifts. That predictability is what lets one pass replace every nested-loop comparison: " +
          "there's never a need to check a pair once you know which single direction the sum needs to move.",
        steps: [
          'Set left = 0 and right = numbers.length − 1.',
          'If numbers[left] + numbers[right] equals target, that\'s the answer.',
          'If the sum is too small, move left forward — the only way to increase it.',
          'If the sum is too big, move right backward — the only way to decrease it.',
        ],
        visual: {
          arrays: [{ label: 'numbers (target = 9)', values: [2, 7, 11, 15] }],
          varNames: [],
          steps: [
            { highlights: { numbers: { primary: [0], secondary: [3] } }, note: '2 + 15 = 17, too big — move right backward.' },
            { highlights: { numbers: { primary: [0], secondary: [2] } }, note: '2 + 11 = 13, still too big — move right backward.' },
            { highlights: { numbers: { primary: [0], secondary: [1] } }, note: '2 + 7 = 9 — match! Return 1-indexed [1, 2], found in 2 comparisons instead of scanning every pair.' },
          ],
        },
        code:
`public int[] twoSum(int[] numbers, int target) {
    int left = 0, right = numbers.length - 1;
    while (left < right) {
        int sum = numbers[left] + numbers[right];
        if (sum == target) {
            return new int[] { left + 1, right + 1 };
        } else if (sum < target) {
            left++;
        } else {
            right--;
        }
    }
    return new int[] {};
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        interviewNote: "Mentioning that a HashMap complement-lookup (the original Two Sum's approach) would also solve this in O(n) time but O(n) space is worth a sentence — the two-pointer version is strictly better here specifically because sortedness is already given.",
        whenToUse:
          "This is the canonical opposite-direction two-pointer pattern — reach for it whenever the input is sorted " +
          "and the question is about a pair (or, with an added outer loop, a triplet) summing to a target.",
      },
    ],
  },
  {
    id: 'container-with-most-water',
    title: 'Container With Most Water',
    difficulty: 'Medium',
    leetcode: { number: 11, url: 'https://leetcode.com/problems/container-with-most-water/' },
    statement:
      'Given n non-negative integers height[0..n-1], where each represents a vertical line at position i, find ' +
      'two lines that, together with the x-axis, form a container that holds the most water. Return the maximum ' +
      'amount of water the container can store.',
    example: {
      input: 'height = [1, 8, 6, 2, 5, 4, 8, 3, 7]',
      output: '49',
      note: 'The lines at index 1 (height 8) and index 8 (height 7) hold 7 × min(8, 7) = 49 units.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Check Every Pair of Lines)',
        why:
          "The direct reading of the problem: try every pair of lines, compute the area between them " +
          "(width × the shorter of the two heights), and keep the maximum. Correct, but every pair is checked " +
          "even when a smarter rule could rule most of them out.",
        steps: [
          'For each pair of indices (i, j), compute width = j − i and height = min(height[i], height[j]).',
          'Compute area = width × height, and keep a running maximum.',
          'Check every possible pair before returning the maximum found.',
          'Every pair is checked — O(n²), no matter how clearly some pairs are worse than others.',
        ],
        code:
`public int maxAreaBruteForce(int[] height) {
    int maxArea = 0;
    for (int i = 0; i < height.length; i++) {
        for (int j = i + 1; j < height.length; j++) {
            int width = j - i;
            int area = width * Math.min(height[i], height[j]);
            maxArea = Math.max(maxArea, area);
        }
    }
    return maxArea;
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
        whenToUse:
          "Only useful to confirm the area formula out loud before optimizing — O(n²) does not scale for this " +
          "problem's typical input sizes.",
      },
      {
        name: 'Approach 2 — Optimized: Two Pointers, Always Move the Shorter Line',
        why:
          "Start with the widest possible container (both ends) and shrink inward one step at a time. The key " +
          "insight: whichever line is currently SHORTER is the one limiting the area — keeping it and moving the " +
          "other line can only ever make things worse (width shrinks, and the shorter line still caps the height). " +
          "So the only line worth ever moving is the shorter one; the taller one might still pair better with something further in.",
        steps: [
          'Set left = 0 and right = height.length − 1 (the widest container possible).',
          'Compute the current area and update the running maximum.',
          'Move whichever pointer points at the SHORTER line — it\'s the one holding the container back.',
          'Repeat until left and right meet; the maximum area seen is the answer.',
        ],
        visual: {
          arrays: [{ label: 'height', values: [1, 8, 6, 2, 5, 4, 8, 3, 7] }],
          varNames: ['maxArea'],
          steps: [
            { highlights: { height: { primary: [0], secondary: [8] } }, vars: { maxArea: 8 }, note: 'left=0 (h=1), right=8 (h=7). Area = 8 × min(1,7) = 8. Left is shorter — move left forward.' },
            { highlights: { height: { primary: [1], secondary: [8] } }, vars: { maxArea: 49 }, note: 'left=1 (h=8), right=8 (h=7). Area = 7 × min(8,7) = 49 — new max! Right is now the shorter line — move right backward.' },
            { highlights: { height: { primary: [1], secondary: [7] } }, vars: { maxArea: 49 }, note: 'left=1 (h=8), right=7 (h=3). Area = 6 × 3 = 18, not better. Right is shorter — move right backward.' },
            { highlights: { height: { primary: [1], secondary: [6] } }, vars: { maxArea: 49 }, note: 'left=1 (h=8), right=6 (h=8). Area = 5 × 8 = 40, not better. Equal heights — move right backward (either side is a valid choice).' },
            { highlights: { height: { primary: [1], secondary: [5] } }, vars: { maxArea: 49 }, note: 'left=1 (h=8), right=5 (h=4). Area = 4 × 4 = 16, not better. Right is shorter — keep moving right.' },
            { highlights: { height: { primary: [1], secondary: [2] } }, vars: { maxArea: 49 }, note: 'The pointers keep converging — every remaining pair (width ≤ 5) is checked, but none beats 49. Final answer: 49.' },
          ],
        },
        code:
`public int maxArea(int[] height) {
    int left = 0, right = height.length - 1;
    int maxArea = 0;
    while (left < right) {
        int width = right - left;
        int area = width * Math.min(height[left], height[right]);
        maxArea = Math.max(maxArea, area);
        if (height[left] < height[right]) {
            left++;
        } else {
            right--;
        }
    }
    return maxArea;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        whenToUse:
          "This greedy \"always move the shorter line\" argument is the heart of the problem — be ready to explain " +
          "WHY moving the taller line can never help (width only shrinks, and the shorter line still caps the area) " +
          "since that justification is usually what the interviewer is actually listening for.",
      },
    ],
  },
  {
    id: 'trapping-rain-water',
    title: 'Trapping Rain Water',
    difficulty: 'Hard',
    leetcode: { number: 42, url: 'https://leetcode.com/problems/trapping-rain-water/' },
    statement:
      'Given n non-negative integers representing an elevation map where the width of each bar is 1, compute ' +
      'how much water it can trap after raining.',
    example: {
      input: 'height = [0, 1, 0, 2, 1, 0, 1, 3, 2, 1, 2, 1]',
      output: '6',
      note: 'Water pools above the lower bars wherever a taller bar exists on both sides — 6 total units.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Scan Both Directions Per Index)',
        why:
          "For every single bar, the water sitting above it is capped by the shorter of its tallest wall to the " +
          "left and its tallest wall to the right. The direct way to get those two walls is to rescan the whole " +
          "array in each direction, for every index.",
        steps: [
          'For each index i, scan left of i to find the tallest bar so far (leftMax).',
          'Scan right of i to find the tallest bar so far (rightMax).',
          'The water above index i is min(leftMax, rightMax) − height[i], if positive.',
          'Sum this over every index — but each index triggers two full rescans, O(n) work each, for O(n²) total.',
        ],
        code:
`public int trapBruteForce(int[] height) {
    int total = 0;
    for (int i = 0; i < height.length; i++) {
        int leftMax = 0;
        for (int l = 0; l <= i; l++) leftMax = Math.max(leftMax, height[l]);
        int rightMax = 0;
        for (int r = i; r < height.length; r++) rightMax = Math.max(rightMax, height[r]);
        total += Math.min(leftMax, rightMax) - height[i];
    }
    return total;
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
        whenToUse:
          "Useful only to establish the core formula — min(leftMax, rightMax) − height[i] — out loud before " +
          "optimizing away the repeated rescans.",
      },
      {
        name: 'Approach 2 — Precompute leftMax[] and rightMax[] Arrays',
        why:
          "The brute force repeats the same left-scan and right-scan work over and over. Precomputing leftMax[i] " +
          "and rightMax[i] for every index up front — each in a single O(n) pass — means every index's water can " +
          "then be read off in O(1), turning the nested rescans into two simple array builds.",
        steps: [
          'Build leftMax[i] = the tallest bar in height[0..i], scanning left to right once.',
          'Build rightMax[i] = the tallest bar in height[i..n-1], scanning right to left once.',
          'For every index, water[i] = min(leftMax[i], rightMax[i]) − height[i].',
          'Sum all the water[i] values for the total — two O(n) passes to build the arrays, one more O(n) pass to sum.',
        ],
        visual: {
          arrays: [
            { label: 'height', values: [0, 1, 0, 2, 1, 0, 1, 3, 2, 1, 2, 1] },
            { label: 'leftMax', values: [null, null, null, null, null, null, null, null, null, null, null, null] },
            { label: 'rightMax', values: [null, null, null, null, null, null, null, null, null, null, null, null] },
            { label: 'water', values: [null, null, null, null, null, null, null, null, null, null, null, null] },
          ],
          varNames: [],
          steps: [
            { snapshots: { leftMax: [0, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 3] }, note: 'Scan left → right, tracking the running max so far: leftMax = [0,1,1,2,2,2,2,3,3,3,3,3].' },
            { snapshots: { rightMax: [3, 3, 3, 3, 3, 3, 3, 3, 2, 2, 2, 1] }, note: 'Scan right → left, tracking the running max: rightMax = [3,3,3,3,3,3,3,3,2,2,2,1].' },
            { snapshots: { water: [0, 0, 1, 0, 1, 2, 1, 0, 0, 1, 0, 0] }, note: 'For every index, water[i] = min(leftMax[i], rightMax[i]) − height[i]. Index 5, for example: min(2,3) − 0 = 2.' },
            { note: 'Sum every water[i]: 0+0+1+0+1+2+1+0+0+1+0+0 = 6 total units trapped — matches the expected answer.' },
          ],
        },
        code:
`public int trapPrecomputed(int[] height) {
    int n = height.length;
    int[] leftMax = new int[n];
    int[] rightMax = new int[n];

    leftMax[0] = height[0];
    for (int i = 1; i < n; i++) leftMax[i] = Math.max(leftMax[i - 1], height[i]);

    rightMax[n - 1] = height[n - 1];
    for (int i = n - 2; i >= 0; i--) rightMax[i] = Math.max(rightMax[i + 1], height[i]);

    int total = 0;
    for (int i = 0; i < n; i++) {
        total += Math.min(leftMax[i], rightMax[i]) - height[i];
    }
    return total;
}`,
        complexity: { time: 'O(n)', space: 'O(n) — two extra arrays' },
        whenToUse:
          "A solid, easy-to-explain O(n) answer. Mention it as the stepping stone toward the two-pointer version " +
          "below, which keeps the same idea but drops the two extra arrays entirely.",
      },
      {
        name: 'Approach 3 — Optimized: Two Pointers, O(1) Space',
        why:
          "The precomputed-array version stores the FULL leftMax and rightMax for every index, but at any moment " +
          "only the smaller of the two current running maxes actually matters — the taller side is guaranteed " +
          "not to be the bottleneck yet. Tracking just two running scalars (leftMax, rightMax) and always " +
          "advancing the pointer on the SHORTER side is enough, because that pointer's final water level can " +
          "never be limited by anything further away on the taller side.",
        steps: [
          'Set left = 0, right = n − 1, and leftMax = rightMax = 0.',
          'If height[left] ≤ height[right], process the left side: update leftMax, add leftMax − height[left] if positive, then move left forward.',
          'Otherwise, process the right side symmetrically: update rightMax, add rightMax − height[right] if positive, then move right backward.',
          'Repeat until left and right meet — the running total is the answer, with no extra arrays at all.',
        ],
        visual: {
          arrays: [{ label: 'height', values: [0, 1, 0, 2, 1, 0, 1, 3, 2, 1, 2, 1] }],
          varNames: ['leftMax', 'rightMax', 'total'],
          steps: [
            { highlights: { height: { primary: [0], secondary: [11] } }, vars: { leftMax: 0, rightMax: 0, total: 0 }, note: 'height[0]=0 ≤ height[11]=1 → process left. leftMax stays 0 (0 ≥ 0), no water. left moves to 1.' },
            { highlights: { height: { primary: [1], secondary: [11] } }, vars: { leftMax: 1, rightMax: 0, total: 0 }, note: 'height[1]=1 ≤ height[11]=1 → process left. leftMax becomes 1, no water yet. left moves to 2.' },
            { highlights: { height: { primary: [2], secondary: [11] } }, vars: { leftMax: 1, rightMax: 0, total: 1 }, note: 'height[2]=0 ≤ height[11]=1 → process left. height[2]=0 < leftMax=1 → trap 1 unit. total=1. left moves to 3.' },
            { highlights: { height: { primary: [3], secondary: [11] } }, vars: { leftMax: 1, rightMax: 1, total: 1 }, note: 'height[3]=2 > height[11]=1 → process right instead. rightMax becomes 1, no water. right moves to 10.' },
            { highlights: { height: { primary: [3], secondary: [10] } }, vars: { leftMax: 2, rightMax: 1, total: 1 }, note: 'height[3]=2 ≤ height[10]=2 → process left. leftMax becomes 2, no water. left moves to 4.' },
            { highlights: { height: { primary: [4], secondary: [10] } }, vars: { leftMax: 2, rightMax: 1, total: 2 }, note: 'height[4]=1 ≤ height[10]=2 → process left. height[4]=1 < leftMax=2 → trap 1 unit. total=2. left moves to 5.' },
            { highlights: { height: { primary: [5], secondary: [10] } }, vars: { leftMax: 2, rightMax: 1, total: 4 }, note: 'height[5]=0 ≤ height[10]=2 → process left. height[5]=0 < leftMax=2 → trap 2 units. total=4. left moves to 6.' },
            { highlights: { height: { primary: [6], secondary: [10] } }, vars: { leftMax: 2, rightMax: 1, total: 5 }, note: 'height[6]=1 ≤ height[10]=2 → process left. height[6]=1 < leftMax=2 → trap 1 unit. total=5. left moves to 7.' },
            { highlights: { height: { primary: [7], secondary: [10] } }, vars: { leftMax: 3, rightMax: 2, total: 5 }, note: 'height[7]=3 > height[10]=2 → process right. rightMax becomes 2, no water. right moves to 9.' },
            { highlights: { height: { primary: [7], secondary: [9] } }, vars: { leftMax: 3, rightMax: 2, total: 6 }, note: 'height[7]=3 > height[9]=1 → process right. height[9]=1 < rightMax=2 → trap 1 unit. total=6. right moves to 8.' },
            { highlights: { height: { primary: [7], secondary: [8] } }, vars: { leftMax: 3, rightMax: 2, total: 6 }, note: 'height[7]=3 > height[8]=2 → process right. rightMax stays 2 (2 ≥ 2), no water. right moves to 7 — left (7) no longer less than right (7), done. Final total: 6.' },
          ],
        },
        code:
`public int trap(int[] height) {
    int left = 0, right = height.length - 1;
    int leftMax = 0, rightMax = 0, total = 0;

    while (left < right) {
        if (height[left] <= height[right]) {
            leftMax = Math.max(leftMax, height[left]);
            total += leftMax - height[left];
            left++;
        } else {
            rightMax = Math.max(rightMax, height[right]);
            total += rightMax - height[right];
            right--;
        }
    }
    return total;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        interviewNote: "The justification for correctness is the part worth saying out loud: whichever side has the smaller height[left]/height[right] value is guaranteed to have its water level capped by its own running max, regardless of what the taller side turns out to be further along.",
        whenToUse:
          "This is the answer to give in an interview — same result as the precomputed-array version, but O(1) " +
          "space by only ever needing to know the SMALLER of the two current running maxes at each step.",
      },
    ],
  },
];

export default twoPointersProblems;
